'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Pencil,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { bookRequestHeaders } from './request-headers';
import { ComicStorySummary } from './ComicStorySummary';
import { ComicDedicationField } from './ComicDedicationField';
import { shouldBlockCheckout, type DedicationPutResult } from './comic-dedication';
import { trackCreate } from '@/utils/create-flow-analytics';
import { httpFailureReason, type FailureReason } from '@/utils/analytics-events';
import { MangaPageScript, MangaSettings } from '@/types/manga';
import { MangaCharacterDraft, MangaPhoto } from './types';
import {
  comicReaderState,
  comicResumeTarget,
  pollFailureAction,
  previewErrorMessage,
  POLL_INVALID_MESSAGE,
  POLL_LOST_MESSAGE,
  comicPreviewPromise,
  comicPreviewFromBook,
  comicStorySummaryFromBook,
  summaryPollDone,
  previewAfterBandChange,
  comicPreviewPollMs,
  comicChangeableBand,
  bandChangeRetry,
  bandChangeErrorMessage,
  BAND_RETRY_MS,
  comicCoverRedrawsLeft,
  coverRedrawNote,
  coverRedrawConfirmText,
  coverRedrawErrorMessage,
  COVER_REDRAW_KEPT_MESSAGE,
  COMIC_MAX_COVER_REDRAWS,
  comicRedrawStuck,
  COVER_REDRAW_RECOVERED_MESSAGE,
} from './reader-state';
import { READER_BAND_ORDER, bandAgeLabel, type ReaderBand } from '@/utils/manga/comic-story-profile';
import { useSiteFlags } from '@/context/SiteFlagsContext';
import { buildComicPreviewPayload, comicStoryIdFor, payloadLanguage, type ComicStoryChoice } from './preview-payload';
import type { TypedRelativesChoice } from '@/utils/manga/family-guard';
import {
  previewInputKey,
  decidePreviewStart,
  readStoredPreviewBook,
  storePreviewBook,
  clearStoredPreviewBook,
  safeSessionStorage,
} from '@/lib/comics/preview-session';

interface MangaGenerateStepProps {
  photo: MangaPhoto;
  characters: MangaCharacterDraft[];
  settings: MangaSettings;
  storyTitle: string;
  storyIdea: string;
  /** Story language code (from the picture-book wizard's list); defaults to English. */
  language?: string;
  /** The parent's answer to the typed-relative notice; sent only while it matches the idea's words. */
  typedRelatives?: TypedRelativesChoice | null;
  onBack: () => void;
  /** Preview books already created this visit, by input key (lives in the page so Back/Next keeps it). */
  knownBookIds?: Record<string, string>;
  /** Report a created book (or null to forget it) to the page. */
  onBookId?: (key: string, bookId: string | null) => void;
  /** `idea-card:<id>` when the title and idea came from an idea card (analytics only). */
  ideaSource?: string;
  /** The chosen catalogue story: its id is sent as storyId while the idea and length are still the story's. */
  story?: ComicStoryChoice | null;
}

interface ComicPreview {
  bookId: string;
  title: string;
  coverImageUrl: string | null;
  pageCount: number;
  logline: string;
  /** The stored "Our story" summary, once written (it can arrive after the preview is ready). */
  storySummary: { heading: string; text: string; starring: string } | null;
  /** Only a flag-off book has a stored page script to show. */
  pages: MangaPageScript[];
  /** Set only while the reader age can still be changed (V2, PREVIEW_READY, nothing drawn). */
  readerBand: ReaderBand | null;
  /** Cover redraws left (0: used up), or null while the cover cannot be redrawn (hides the button). */
  redrawsLeft: number | null;
}

type Phase = 'creating' | 'preview' | 'error';

const POLL_TIMEOUT_MS = 6 * 60 * 1000;

const FAST_LOADING_MESSAGES = [
  'Meeting your heroes...',
  'Planning the story...',
  'Designing the costumes...',
  'Drawing the cover art...',
  'Lettering the title...',
];

const LOADING_MESSAGES = [
  'Meeting your heroes...',
  'Planning the panels...',
  'Sketching dramatic poses...',
  'Adding speech balloons...',
  'Dropping in speed lines and sound effects...',
  'Drawing the cover art...',
];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** A stored preview book was bought meanwhile: the page is navigating to its reader. */
class RedirectedToReader extends Error {}

class PreviewError extends Error {
  goBack: boolean;
  /** The book is unusable (failed or gone): forget it so the next attempt makes a new one. */
  discard: boolean;
  /** Analytics failure code (never the message). */
  reason: FailureReason;
  constructor(message: string, goBack = false, discard = false, reason: FailureReason = 'error') {
    super(message);
    this.goBack = goBack;
    this.discard = discard;
    this.reason = reason;
  }
}

export function MangaGenerateStep({
  photo,
  characters,
  settings,
  storyTitle,
  storyIdea,
  language = 'en',
  typedRelatives = null,
  onBack,
  knownBookIds,
  onBookId,
  ideaSource,
  story = null,
}: MangaGenerateStepProps) {
  const router = useRouter();
  const siteFlags = useSiteFlags();
  const fast = siteFlags.comicFastPreview || settings.pageCount === 24;
  const loadingMessages = fast ? FAST_LOADING_MESSAGES : LOADING_MESSAGES;

  const [phase, setPhase] = useState<Phase>('creating');
  const [preview, setPreview] = useState<ComicPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorGoBack, setErrorGoBack] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [expandedPage, setExpandedPage] = useState<number | null>(null);
  const [scriptOpen, setScriptOpen] = useState(false);
  const [bandPickerOpen, setBandPickerOpen] = useState(false);
  const [bandBusy, setBandBusy] = useState(false);
  const [bandWaiting, setBandWaiting] = useState<ReaderBand | null>(null);
  const [bandError, setBandError] = useState<string | null>(null);
  const [redrawBusy, setRedrawBusy] = useState(false);
  const [redrawError, setRedrawError] = useState<string | null>(null);
  // "Redraw the cover" asks first: the old cover is gone for good once redrawn
  const [redrawConfirmOpen, setRedrawConfirmOpen] = useState(false);

  const startedRef = useRef(false);
  const dedicationSave = useRef<Promise<DedicationPutResult | null>>(Promise.resolve(null));
  const cancelledRef = useRef(false);

  // Rotate the loading messages while the comic is being made
  useEffect(() => {
    if (phase !== 'creating') return;
    const interval = setInterval(
      () => setLoadingMessageIndex((i) => (i + 1) % loadingMessages.length),
      4000
    );
    return () => clearInterval(interval);
  }, [phase, loadingMessages.length]);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  /** Poll the book until the preview is ready to buy (or has failed). */
  const pollUntilPreview = async (
    bookId: string,
    headers: Record<string, string>
  ): Promise<ComicPreview> => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    let failures = 0;
    let recoveryAsked = false;
    while (Date.now() < deadline) {
      await sleep(comicPreviewPollMs(fast));
      // Checked after the sleep and before the fetch: no GET fires after unmount.
      if (cancelledRef.current) throw new PreviewError('cancelled');
      let book: any = null;
      let failure: number | 'network' | null = null;
      try {
        const res = await fetch(`/api/books/${bookId}`, { headers, cache: 'no-store' });
        if (res.ok) book = (await res.json())?.book ?? null;
        else failure = res.status;
      } catch {
        failure = 'network';
      }
      if (failure !== null) {
        failures += 1;
        const action = pollFailureAction(failure, failures);
        if (action === 'stop-invalid') throw new PreviewError(POLL_INVALID_MESSAGE, true, true);
        if (action === 'stop-lost') throw new PreviewError(POLL_LOST_MESSAGE);
        continue;
      }
      failures = 0;
      // A bought comic (drawing, ready or retrying) is read at /comic/<id>, not previewed here.
      if (comicResumeTarget(book?.status) === 'reader') {
        router.replace(`/comic/${encodeURIComponent(bookId)}`);
        throw new RedirectedToReader();
      }
      // A cover redraw that died (left DRAFT for over 17 minutes): ask the server once to put the preview back
      if (!recoveryAsked && comicRedrawStuck(book)) {
        recoveryAsked = true;
        await fetch(`/api/books/${encodeURIComponent(bookId)}/comic-cover-redraw`, { method: 'POST', headers }).catch(() => undefined);
        continue;
      }
      const state = comicReaderState(book);
      if (state === 'failed') {
        throw new PreviewError("We couldn't finish your comic this time. Please try again.", false, true);
      }
      if (state === 'awaiting-purchase') {
        const p = comicPreviewFromBook(book, { title: storyTitle, pageCount: settings.pageCount });
        return {
          bookId,
          title: p.title,
          coverImageUrl: p.coverImageUrl,
          pageCount: p.pageCount,
          logline: p.logline,
          storySummary: comicStorySummaryFromBook(book),
          pages: p.legacyPages,
          readerBand: comicChangeableBand(book),
          redrawsLeft: comicCoverRedrawsLeft(book),
        };
      }
    }
    throw new PreviewError('This is taking longer than expected. Please try again in a moment.', false, false, 'timeout');
  };

  /** Change who the story is written for. The server rewrites the script (cover kept) or re-runs the preview. */
  const changeReaderBand = async (band: ReaderBand) => {
    if (!preview || band === preview.readerBand || bandBusy || redrawBusy) return;
    setBandBusy(true);
    setBandError(null);
    try {
      const headers = await bookRequestHeaders();
      const started = Date.now();
      let res: Response;
      let data: any;
      for (;;) {
        res = await fetch(`/api/books/${preview.bookId}/comic-reader-band`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ band }),
        });
        data = await res.json().catch(() => ({}));
        if (res.ok || bandChangeRetry(res.status, data, Date.now() - started) === 'stop') break;
        // Busy: the story is being written. Retry quietly every 10 s (stops on unmount).
        setBandWaiting(band);
        await sleep(Math.max(BAND_RETRY_MS, Number(data?.retryAfterMs) || 0));
        if (cancelledRef.current) return;
      }
      setBandWaiting(null);
      if (!res.ok) {
        setBandError(bandChangeErrorMessage(data));
        return;
      }
      setBandPickerOpen(false);
      if (data?.status === 'regenerating-preview') {
        // A bigger jump re-runs the preview: back to the generating state, then poll as usual
        setPhase('creating');
        const ready = await pollUntilPreview(preview.bookId, headers);
        if (cancelledRef.current) return;
        setPreview(ready);
        setPhase('preview');
      } else {
        setPreview(previewAfterBandChange(preview, band));
      }
    } catch (e) {
      if (cancelledRef.current || e instanceof RedirectedToReader) return;
      if (e instanceof PreviewError) {
        setErrorGoBack(e.goBack);
        setError(e.message);
        setPhase('error');
      } else {
        setBandError('We could not change that. Please try again.');
      }
    } finally {
      setBandWaiting(null);
      setBandBusy(false);
    }
  };

  /** Redraw the cover (same story). The cover shows a spinner while the new one is drawn, then the book is re-read. */
  const redrawCover = async () => {
    if (!preview || redrawBusy || bandBusy || !preview.redrawsLeft) return;
    const before = preview;
    setRedrawBusy(true);
    setRedrawError(null);
    try {
      const headers = await bookRequestHeaders();
      const started = Date.now();
      let res: Response;
      let data: any;
      for (;;) {
        res = await fetch(`/api/books/${encodeURIComponent(before.bookId)}/comic-cover-redraw`, {
          method: 'POST',
          headers: { ...headers },
        });
        data = await res.json().catch(() => ({}));
        if (res.ok || bandChangeRetry(res.status, data, Date.now() - started) === 'stop') break;
        // Busy: the story is being written. Retry quietly (stops on unmount).
        await sleep(Math.max(BAND_RETRY_MS, Number(data?.retryAfterMs) || 0));
        if (cancelledRef.current) return;
      }
      if (!res.ok) {
        setRedrawError(coverRedrawErrorMessage(data));
        if (res.status === 429) setPreview({ ...before, redrawsLeft: 0 });
        return;
      }
      if (data?.status === 'recovered') {
        // An earlier redraw had died: the server put the cover back (not counted). The parent can simply try again
        const ready = await pollUntilPreview(before.bookId, headers);
        if (cancelledRef.current) return;
        setPreview(ready);
        setRedrawError(COVER_REDRAW_RECOVERED_MESSAGE);
        return;
      }
      trackCreate('comic', 'cover_redraw', { count: COMIC_MAX_COVER_REDRAWS - Number(data?.redrawsLeft) });
      setPreview({ ...before, redrawsLeft: Number(data?.redrawsLeft) });
      const ready = await pollUntilPreview(before.bookId, headers);
      if (cancelledRef.current) return;
      setPreview(ready);
      // A failed redraw keeps the old cover (the server puts it back)
      if (ready.coverImageUrl === before.coverImageUrl) setRedrawError(COVER_REDRAW_KEPT_MESSAGE);
    } catch (e) {
      if (cancelledRef.current || e instanceof RedirectedToReader) return;
      if (e instanceof PreviewError) {
        setErrorGoBack(e.goBack);
        setError(e.message);
        setPhase('error');
      } else {
        setRedrawError(coverRedrawErrorMessage(null));
      }
    } finally {
      setRedrawBusy(false);
    }
  };

  const inputKey = () =>
    previewInputKey({
      characters,
      storyTitle,
      storyIdea,
      settings,
      photoDataUrl: photo.dataUrl,
      language,
      // A stamped and a free-form comic with the same text are different books
      storyId: comicStoryIdFor(story, storyIdea, settings.pageCount, payloadLanguage(language)),
    });

  const createPreview = async () => {
    const startedAt = Date.now();
    setPhase('creating');
    setError(null);
    setErrorGoBack(false);

    try {
      const headers = await bookRequestHeaders();

      // A book already made for these exact inputs is re-polled; a new one is created only when none exists.
      const key = inputKey();
      const storage = safeSessionStorage();
      const start = decidePreviewStart(knownBookIds?.[key], readStoredPreviewBook(storage, key));
      const forget = () => {
        clearStoredPreviewBook(storage, key);
        onBookId?.(key, null);
      };
      if (start.kind === 'poll') {
        try {
          const ready = await pollUntilPreview(start.bookId, headers);
          if (cancelledRef.current) return;
          setPreview(ready);
          setPhase('preview');
          trackCreate('comic', 'preview_ready', { ms: Date.now() - startedAt, source: 'resumed' });
          return;
        } catch (e) {
          // Failed or vanished: fall through and make a new one. A timeout or lost connection keeps the book.
          if (e instanceof RedirectedToReader) return;
          if (!(e instanceof PreviewError && e.discard)) throw e;
          forget();
        }
      }

      const payload = buildComicPreviewPayload({ characters, storyTitle, storyIdea, settings, language, photo, typedRelatives, story });

      trackCreate('comic', 'preview_requested', ideaSource ? { source: ideaSource } : undefined);
      const res = await fetch('/api/books/create-manga-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (res.status !== 202 || !data?.bookId) {
        const { message, goBack } = previewErrorMessage(res.status, data);
        throw new PreviewError(message, goBack, false, httpFailureReason(res.status));
      }

      storePreviewBook(storage, key, data.bookId as string);
      onBookId?.(key, data.bookId as string);
      let ready: ComicPreview;
      try {
        ready = await pollUntilPreview(data.bookId as string, headers);
      } catch (e) {
        if (e instanceof PreviewError && e.discard) forget();
        throw e;
      }
      if (cancelledRef.current) return;
      setPreview(ready);
      setPhase('preview');
      trackCreate('comic', 'preview_ready', { ms: Date.now() - startedAt, source: 'new' });
    } catch (e) {
      if (cancelledRef.current || e instanceof RedirectedToReader) return;
      trackCreate('comic', 'preview_failed', { reason: e instanceof PreviewError ? e.reason : 'network' });
      setErrorGoBack(e instanceof PreviewError && e.goBack);
      setError(
        e instanceof PreviewError
          ? e.message
          : 'Something went wrong while starting your comic. Please try again.'
      );
      setPhase('error');
    }
  };

  useEffect(() => {
    // Guard against React 18 double-effect in dev: creation is expensive
    if (startedRef.current) return;
    startedRef.current = true;
    createPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The "Our story" summary is written after the preview is ready: re-fetch the book every 15 s (at most 12 times)
  // until it arrives or the server records an outcome. Nothing runs when the summary is already shown.
  const summaryBookId = preview?.bookId ?? null;
  const hasSummary = !!preview?.storySummary;
  // A reader-band change clears the summary and must restart a poll that had already run out.
  const summaryBand = preview?.readerBand ?? null;
  useEffect(() => {
    if (!summaryBookId || hasSummary) return;
    let stopped = false;
    let busy = false;
    let attempts = 0;
    const timer = setInterval(async () => {
      if (stopped || busy) return;
      attempts += 1;
      if (attempts > 12) {
        clearInterval(timer);
        return;
      }
      busy = true;
      try {
        const headers = await bookRequestHeaders();
        const res = await fetch(`/api/books/${summaryBookId}`, { headers, cache: 'no-store' });
        if (!res.ok || stopped) return;
        const book = (await res.json())?.book ?? null;
        if (stopped) return;
        if (summaryPollDone(book)) {
          clearInterval(timer);
          const found = comicStorySummaryFromBook(book);
          if (found) setPreview((cur) => (cur && cur.bookId === summaryBookId ? { ...cur, storySummary: found } : cur));
        }
      } catch {
        // a missed poll is retried on the next tick
      } finally {
        busy = false;
      }
    }, 15_000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [summaryBookId, hasSummary, summaryBand]);

  // Interior pages are the paid product: checkout drives generation.
  const goToCheckout = async () => {
    if (!preview) return;
    // A slow or hung dedication save never holds checkout for more than 3 s (timeout -> navigate).
    // A save that FAILED (e.g. too many lines) stops checkout: the error is shown under the field.
    const r = await Promise.race<DedicationPutResult | null>([
      dedicationSave.current.catch(() => null),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
    ]);
    if (shouldBlockCheckout(r)) {
      const el = document.getElementById('comic-dedication-field');
      el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      document.getElementById('comic-dedication')?.focus();
      return;
    }
    router.push(`/checkout?bookId=${preview.bookId}&flow=comic`);
  };

  // ---------- Creating ----------
  if (phase === 'creating') {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-14 px-6 text-center space-y-4">
            <div className="relative w-16 h-16 mx-auto">
              <div className="absolute inset-0 rounded-full border-4 border-purple-100" />
              <div className="absolute inset-0 rounded-full border-4 border-purple-600 border-t-transparent animate-spin" />
              <Pencil className="absolute inset-0 m-auto w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Creating your comic</h2>
            <p className="text-purple-700 font-medium">{loadingMessages[loadingMessageIndex]}</p>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              {fast
                ? 'Our story team is planning '
                : 'Our story team is planning every panel, balloon and sound effect of '}
              &ldquo;{storyTitle.trim()}&rdquo; and drawing the cover.{' '}
              {fast
                ? 'This usually takes about a minute.'
                : 'This can take a few minutes, so please keep this tab open.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------- Error ----------
  if (phase === 'error') {
    return (
      <div className="space-y-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-10 px-6 text-center space-y-4">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
            <h2 className="text-xl font-bold text-gray-800">We couldn&apos;t make your comic</h2>
            <p className="text-sm text-red-700 max-w-md mx-auto">{error}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 pt-2">
              <Button variant="outline" onClick={onBack}>
                Back to Style
              </Button>
              {!errorGoBack && (
                <Button
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={createPreview}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try again
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!preview) return null;

  const total = preview.pageCount;

  // ---------- Preview ----------
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">{preview.title}</h2>
        {preview.logline && (
          <p className="text-gray-600 max-w-xl mx-auto italic">{preview.logline}</p>
        )}
        {preview.storySummary && (
          <div className="max-w-xl mx-auto">
            <ComicStorySummary summary={preview.storySummary} defaultOpen />
          </div>
        )}
        {preview.readerBand && (
          <p className="text-sm text-gray-500">
            Written for ages {bandAgeLabel(preview.readerBand)} &middot;{' '}
            {bandPickerOpen ? (
              <select
                aria-label="Reader age"
                className="border border-gray-300 rounded px-1 py-0.5 text-sm text-gray-700 bg-white"
                value={preview.readerBand}
                disabled={bandBusy}
                onChange={(e) => changeReaderBand(e.target.value as ReaderBand)}
              >
                {READER_BAND_ORDER.map((b) => (
                  <option key={b} value={b}>
                    Ages {bandAgeLabel(b)}
                  </option>
                ))}
              </select>
            ) : (
              <button
                type="button"
                className="underline text-purple-700 hover:text-purple-900"
                onClick={() => setBandPickerOpen(true)}
              >
                change
              </button>
            )}
            {bandBusy && (
              <span className="ml-2">
                {bandWaiting ? `Updating the story for ages ${bandAgeLabel(bandWaiting)}…` : 'Updating...'}
              </span>
            )}
            {bandError && <span className="ml-2 text-red-600">{bandError}</span>}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {preview.coverImageUrl ? (
              <div className="relative">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={preview.coverImageUrl}
                  alt={`${preview.title} cover`}
                  className={`w-full h-auto object-contain bg-gray-50 ${redrawBusy ? 'opacity-40' : ''}`}
                />
                {redrawBusy && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-purple-800" role="status">
                    <div className="w-10 h-10 rounded-full border-4 border-purple-600 border-t-transparent animate-spin" />
                    <p className="font-medium bg-white/80 rounded px-2">Drawing a new cover... about a minute</p>
                  </div>
                )}
              </div>
            ) : (
              <div className={`${settings.pageCount === 24 ? 'aspect-square' : 'aspect-[3/4]'} flex items-center justify-center bg-gray-100 text-gray-500 text-sm`}>
                Cover is on its way...
              </div>
            )}
            {preview.redrawsLeft !== null && (
              <div className="p-4 border-t border-gray-100 text-center space-y-2">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto"
                  disabled={redrawBusy || bandBusy || preview.redrawsLeft <= 0 || redrawConfirmOpen}
                  onClick={() => {
                    setRedrawError(null);
                    setRedrawConfirmOpen(true);
                  }}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${redrawBusy ? 'animate-spin' : ''}`} />
                  Redraw the cover
                </Button>
                {redrawConfirmOpen && !redrawBusy && preview.redrawsLeft > 0 ? (
                  <div
                    className="border border-purple-200 bg-purple-50 rounded-md p-3 space-y-2 text-sm text-purple-900 text-left"
                    role="alertdialog"
                    aria-label="Redraw the cover?"
                  >
                    <p>{coverRedrawConfirmText(preview.redrawsLeft)}</p>
                    <div className="flex justify-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => setRedrawConfirmOpen(false)}>
                        Keep this cover
                      </Button>
                      <Button
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        disabled={bandBusy}
                        onClick={() => {
                          setRedrawConfirmOpen(false);
                          void redrawCover();
                        }}
                      >
                        Yes, redraw it
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">{coverRedrawNote(preview.redrawsLeft)}</p>
                )}
                {redrawError && <p className="text-sm text-red-600" role="alert">{redrawError}</p>}
              </div>
            )}
          </CardContent>
        </Card>

        {preview.pages.length > 0 ? (
          <Card>
            <CardContent className="p-4 space-y-3">
              <button
                type="button"
                className="w-full flex items-center justify-between text-left font-semibold text-gray-800"
                onClick={() => setScriptOpen((open) => !open)}
              >
                <span className="flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2 text-purple-600" />
                  Story preview ({preview.pages.length} pages)
                </span>
                {scriptOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {scriptOpen && (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {preview.pages.map((page) => {
                    const isExpanded = expandedPage === page.pageNumber;
                    return (
                      <div key={page.pageNumber} className="border border-gray-200 rounded-md">
                        <button
                          type="button"
                          className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                          onClick={() => setExpandedPage(isExpanded ? null : page.pageNumber)}
                        >
                          <span>
                            Page {page.pageNumber}
                            <span className="text-gray-400 font-normal ml-2">
                              {page.panels.length} {page.panels.length === 1 ? 'panel' : 'panels'}
                              {page.cliffhanger ? ' · cliffhanger!' : ''}
                            </span>
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 shrink-0" />
                          ) : (
                            <ChevronDown className="w-4 h-4 shrink-0" />
                          )}
                        </button>
                        {isExpanded && (
                          <div className="px-3 pb-3 space-y-3 text-sm">
                            {page.panels.map((panel) => (
                              <div key={panel.panel} className="border-l-2 border-purple-200 pl-3">
                                <p className="text-xs uppercase tracking-wide text-purple-600 font-semibold">
                                  Panel {panel.panel} · {panel.shot}
                                </p>
                                <p className="text-gray-600">{panel.sceneDescription}</p>
                                {panel.dialogue.map((line, i) => (
                                  <p key={i} className="text-gray-800 mt-1">
                                    <span className="font-semibold">{line.speaker}:</span>{' '}
                                    &ldquo;{line.text}&rdquo;
                                    <span className="text-gray-400 text-xs ml-1">({line.balloon})</span>
                                  </p>
                                ))}
                                {panel.sfx && (
                                  <p className="text-orange-600 font-bold mt-1">SFX: {panel.sfx}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-4 space-y-2 text-center">
              <p className="font-semibold text-gray-800">{preview.title}</p>
              {preview.logline && <p className="text-gray-600 italic">{preview.logline}</p>}
              {preview.storySummary && <ComicStorySummary summary={preview.storySummary} defaultOpen />}
              <p className="text-sm text-gray-600">{comicPreviewPromise(total)}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Checkout CTA: interior pages are drawn after purchase */}
      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="py-4 px-6 text-center">
          <p className="text-sm text-gray-700">
            Love the cover? Complete checkout and we&apos;ll write and draw all{' '}
            <span className="font-semibold">{total} comic pages</span> and email you the finished
            book.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-4 px-6">
          <ComicDedicationField bookId={preview.bookId} pendingSave={dedicationSave} />
        </CardContent>
      </Card>

      <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
        <Button variant="outline" size="lg" onClick={onBack} className="w-full sm:w-auto">
          Back
        </Button>
        <Button
          size="lg"
          className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto"
          onClick={goToCheckout}
          disabled={redrawBusy}
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Get my full comic: Checkout
        </Button>
      </div>
    </div>
  );
}
